import React, {useState, Fragment, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import {entities} from '../../../../../../../util/uiconstants';
import Utility from '../../../../../../../util/utilityfunctions';
import {refreshUnrelatedItControls} from '../../../../../../../actions/ITControl/itcontrolaction';
import {getItControlsSlice, getBodyMetadataSlice} from '../../../../../../../selectors/ISA315/selectors';
import {itProcessShape} from '../../../../shapes';
import {openItRiskModal} from '../CreateUpdateEntities/createUpdateEntities.utils';
import {applyFilters} from '../Common/clientFilters.utils';
import Ellipses from '../../../../../EllipsesControl/EllipsesPresets';
import StyledBodySpecialAccordionContentListItem from '../StyledBodySpecialAccordionContentListItem';
import ISA315ItRiskAccordionControlsItem from './ISA315ItRisksAccordionControlsItem';
import ISA315ItGCItRiskItemNoDataPlaceholder from './ISA315ItGCItRiskItemNoDataPlaceholder';
import CreateEditITRiskModalWrapper from '../../../../../../../components/Common/EntityCrud/CreateEditITRiskModalWrapper';
import CreateEditITControlModalWrapper from '../../../../../../../components/Common/EntityCrud/CreateEditITControlModalWrapper';

export function ISA315ItRiskAccordionContentItem({itRiskId, engagementId, bodyId, itProcess, openRelateModal}) {
	const dispatch = useDispatch();

	const itRisk = useSelector((state) => state.bodiesSpecial.itRisks?.byId[itRiskId]);

	const itControlsById = useSelector((state) => getItControlsSlice(state)?.byId);
	const clientFilters = useSelector((state) => getBodyMetadataSlice(state, bodyId)?.clientFilters);
	const [showITGCModal, setShowITGCModal] = useState(false);
	const [showITRiskModal, setShowITRiskModal] = useState(false);
	const [containerProps, setContainerProps] = useState({});

	let relatedItGcs;
	if (itRisk && itRisk.itControls?.length > 0) {
		// Sort data by name
		const itControls = itRisk.itControls.map((id) => itControlsById[id]);
		Utility.sortAlphaNumeric(itControls, 'itControlName');
		const sortedControls = itControls.map((itControl) => itControl.id);

		relatedItGcs = applyFilters(entities.ItControl, clientFilters, sortedControls, itControlsById);
	}

	const onCreateUpdate = useCallback((clickArgs) => {
		setContainerProps(clickArgs);
		if (clickArgs.entityId === entities.ItRisk) {
			setShowITRiskModal(true);
		} else if (clickArgs.entityId === entities.ItControl) {
			setShowITGCModal(true);
		}
	}, []);

	const onEditItRiskClick = useCallback(() => {
		openItRiskModal(itProcess.id, itProcess.itProcessName, onCreateUpdate, itRisk);
	}, [itRisk, itProcess, onCreateUpdate]);

	const onEditItControlClick = useCallback((clickArgs) => {
		setContainerProps({...clickArgs, isEdit: true});
		setShowITGCModal(true);
	}, []);

	const onItControlUpdated = useCallback(
		(itControl) => {
			return dispatch(refreshUnrelatedItControls(bodyId));
		},
		[bodyId]
	);

	return (
		<Fragment>
			<StyledBodySpecialAccordionContentListItem>
				<StyledItRiskColumn className="StyledItApplicationColumn">
					<span className="styledItApplicationColumnText" onClick={onEditItRiskClick}>
						<Ellipses.Single>{itRisk.itRiskName}</Ellipses.Single>
					</span>
				</StyledItRiskColumn>
			</StyledBodySpecialAccordionContentListItem>
			{relatedItGcs && relatedItGcs.length > 0 ? (
				relatedItGcs.map((itControlId) => {
					return (
						<ISA315ItRiskAccordionControlsItem
							itControlId={itControlId}
							onEditITControl={onEditItControlClick}
							engagementId={engagementId}
						/>
					);
				})
			) : (
				<ISA315ItGCItRiskItemNoDataPlaceholder
					openRelateModal={openRelateModal}
					onCreateUpdate={onCreateUpdate}
					itRisk={itRisk}
					bodyId={bodyId}
				/>
			)}
			{showITRiskModal && (
				<CreateEditITRiskModalWrapper
					isEdit
					entityId={containerProps.entityUId}
					showModal={showITRiskModal}
					setShowModal={setShowITRiskModal}
				/>
			)}
			{showITGCModal && (
				<CreateEditITControlModalWrapper
					isITGC
					itProcessId={itProcess.id}
					itProcessName={itProcess.itProcessName}
					parentEntityId={itRisk.id}
					parentEntityName={itRisk.itRiskName}
					isCreate={containerProps.isEditMode}
					isEdit={containerProps.isEdit}
					entityId={containerProps.entityUId}
					showModal={showITGCModal}
					setShowModal={setShowITGCModal}
					entityType={entities.ItControl}
					onItControlUpdated={onItControlUpdated}
				/>
			)}
		</Fragment>
	);
}

ISA315ItRiskAccordionContentItem.propTypes = {
	itRiskId: PropTypes.number,
	engagementId: PropTypes.number,
	documentId: PropTypes.string,
	bodyId: PropTypes.number,
	documentEngagementId: PropTypes.string,
	itProcess: itProcessShape
};

export default ISA315ItRiskAccordionContentItem;

const StyledItRiskColumn = styled.section`
	width: calc(100% - var(--px-350));
	padding-right: var(--px-10);
	.styledItApplicationColumnText {
		color: var(--blue-600);
		cursor: pointer;
		.ellipses {
			width: auto;
		}
		&:hover {
			.ellipses {
				text-decoration: underline;
			}
		}
	}
`;
